import React, { useState } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../../shared/Crud';
import AddressStepForm from './AddressStepForm';
import ResponsibleReceiptModal from '../ResponsibleReceiptModal';
import deliveryService from '../../../services/deliveryService';
import MessageModal from '../../shared/MessageModal';
import {
    StepFormWrapper,
    FormBlockSeparator,
    FormBlockTitle,
    DateTimeRow,
    TextAreaStyle,
    splitDateTime,
    combineDateTime,
} from './DetailSectionShared';

const { FormGroup, Button } = CrudStyles;

const ResponsibleDisplay = styled.div`
    padding: 12px 16px;
    background: #f8fafc;
    border-radius: 8px;
    margin-bottom: 12px;
    font-size: 14px;
    color: #374151;
    & strong { display: block; margin-bottom: 4px; }
`;
const BtnGroup = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

/**
 * Step de endereço de entrega + dados da entrega (data/hora, instruções) + responsável pelo recebimento.
 * O usuário deve clicar em Salvar/Atualizar para persistir.
 */
export default function DeliveryAddressStep({
    deliveryId,
    customerIdForApi,
    address,
    onChange,
    errors = {},
    pickupDateTime = '',
    deliveryDateTime = '',
    deliveryInstructions = '',
    onDeliveryDateTimeChange,
    onDeliveryInstructionsChange,
    recipientId = null,
    recipientName = '',
    recipientDocument = '',
    recipientPhone = '',
    onResponsibleSelect,
    onClearResponsible,
    customerId = null,
    isTransportCompany,
    addressSaved = false,
    onSaveSuccess,
}) {
    const [showResponsibleModal, setShowResponsibleModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const pickup = splitDateTime(pickupDateTime);
    const delivery = splitDateTime(deliveryDateTime);
    const hasResponsible = !!(recipientId || (recipientName && recipientName.trim()));

    const handleSelect = (selected) => {
        onResponsibleSelect?.(selected);
        setShowResponsibleModal(false);
    };

    const handleSave = async () => {
        if (!deliveryId) {
            setMessageModal({ open: true, title: 'Aviso', message: 'Crie a entrega antes de salvar o endereço de entrega.' });
            return;
        }
        const deliveryAddr = address || {};
        const zipCode = String(deliveryAddr.zipCode || '').replace(/\D/g, '');
        if (zipCode.length !== 8) {
            setMessageModal({ open: true, title: 'Aviso', message: 'Informe um CEP válido (8 dígitos) para salvar.' });
            return;
        }
        setSaving(true);
        try {
            const resp = recipientId
                ? { id: recipientId }
                : (recipientName?.trim()
                    ? { name: recipientName.trim(), document: recipientDocument || null, phoneNumber: recipientPhone || null }
                    : null);
            await deliveryService.updateAddress(deliveryId, deliveryAddr, 'DELIVERY', {
                customerId: customerIdForApi,
                dateTime: deliveryDateTime || null,
                instructions: deliveryInstructions || null,
                responsible: resp,
            });
            onSaveSuccess?.();
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao salvar endereço de entrega.',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <StepFormWrapper>
            <AddressStepForm
                address={address || {}}
                onChange={onChange}
                errors={errors}
            />

            <FormBlockSeparator>
                <FormBlockTitle>Data e instruções de entrega</FormBlockTitle>
                <FormGroup>
                    <label>{isTransportCompany ? 'Data e hora da entrega' : 'Data da entrega'}</label>
                    <DateTimeRow>
                        <FormGroup>
                            <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>Data</label>
                            <input
                                type="date"
                                value={delivery.date}
                                min={pickup.date || undefined}
                                onChange={(e) => onDeliveryDateTimeChange?.(combineDateTime(e.target.value, delivery.time))}
                                title={pickup.date ? 'A data da entrega deve ser no mesmo dia ou após a data de coleta' : ''}
                            />
                        </FormGroup>
                        {isTransportCompany && (
                            <FormGroup>
                                <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>Horário</label>
                                <input
                                    type="time"
                                    value={delivery.time}
                                    onChange={(e) => onDeliveryDateTimeChange?.(combineDateTime(delivery.date, e.target.value))}
                                />
                            </FormGroup>
                        )}
                    </DateTimeRow>
                </FormGroup>
                <FormGroup>
                    <label>Instruções de entrega</label>
                    <textarea
                        rows={2}
                        value={deliveryInstructions || ''}
                        onChange={(e) => onDeliveryInstructionsChange?.(e.target.value)}
                        placeholder="Ex.: Horário preferencial, portaria..."
                        style={TextAreaStyle}
                    />
                </FormGroup>
            </FormBlockSeparator>

            <FormBlockSeparator>
                <FormBlockTitle>Responsável pelo recebimento</FormBlockTitle>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Selecione um responsável existente ou crie um novo. Não é possível editar após a seleção.
                </p>
                {hasResponsible ? (
                    <>
                        <ResponsibleDisplay>
                            <strong>{recipientName || '—'}</strong>
                            {recipientDocument ? <span>CPF: {recipientDocument}</span> : null}
                            {recipientPhone ? <span style={{ display: 'block', marginTop: '4px' }}>Tel: {recipientPhone}</span> : null}
                        </ResponsibleDisplay>
                        <BtnGroup>
                            <Button type="button" variant="outline" onClick={() => setShowResponsibleModal(true)}>
                                Alterar responsável
                            </Button>
                            <Button type="button" variant="outline" onClick={onClearResponsible}>
                                Limpar
                            </Button>
                        </BtnGroup>
                    </>
                ) : (
                    <Button type="button" variant="outline" onClick={() => setShowResponsibleModal(true)}>
                        Selecionar ou criar responsável
                    </Button>
                )}
                <FormGroup style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving}
                        style={{ justifyContent: 'center' }}
                    >
                        {saving ? 'Salvando...' : addressSaved ? 'Atualizar' : 'Salvar'}
                    </Button>
                </FormGroup>
            </FormBlockSeparator>

            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}

            {showResponsibleModal && (
                <ResponsibleReceiptModal
                    customerId={customerId}
                    onSelect={handleSelect}
                    onClose={() => setShowResponsibleModal(false)}
                />
            )}
        </StepFormWrapper>
    );
}
